/** Left Toolbar Reorder — persistence layer
 * (VISTOERA_PRODUCTION_CANVAS_TYPED_DATAFLOW_TEXT_ASSET_LINEAGE_HIGHLIGHT_AND_TOOL_REORDER_P0
 * §37–§44).
 *
 * TOOLBAR_ORDER_SCOPE = LOCAL (browser `localStorage`) until a server-side
 * tenant preference exists.
 *
 * Canonical inheritance (VISTOERA_CANVAS_NODE_CATALOG_TENANT_DEFAULT_ORDER_…
 * P0):
 *   - No override → effective order = live platform catalog order
 *   - Override present → tenant order, with missing nodes appended by
 *     current platform order (deterministic merge)
 *   - "Restore default" → DELETE override (clear localStorage), then
 *     inherit current platform order — never copy a snapshot back into
 *     storage (that would freeze inheritance).
 */

import { LEGACY_HIDDEN_FROM_ADD_NODE_CATALOG } from './canvasNodeCatalog'
import { getCachedNodeCatalogEntries } from './nodeCatalogRuntime'
import { NODE_DEFINITIONS } from './nodeRegistry'

export const TOOLBAR_ORDER_STORAGE_KEY = 'vistoera.canvas.toolbarOrder.v1'

const REGISTRY_ORDER: string[] = NODE_DEFINITIONS.map((d) => d.nodeType).filter(
  (t) => !LEGACY_HIDDEN_FROM_ADD_NODE_CATALOG.has(t),
)

/** Live platform (tenant-visible) catalog order when cache is READY.
 * Before READY (or after a failed load), return [] so the left rail never
 * briefly materialises the full local registry (VistoeraTASK-00583). */
export function defaultToolbarOrder(): string[] {
  const catalog = getCachedNodeCatalogEntries()
  if (catalog.length === 0) return []
  const known = new Set(REGISTRY_ORDER)
  const fromCatalog: string[] = []
  const seen = new Set<string>()
  for (const e of catalog) {
    const t = e.node_type
    // Tenant catalog API already filters OFFLINE / NOT_READY / non-creatable.
    if (!known.has(t) || seen.has(t)) continue
    seen.add(t)
    fromCatalog.push(t)
  }
  // Do NOT append registry-only types missing from the tenant catalog —
  // those are typically OFFLINE / NOT_READY and must not occupy palette slots.
  return fromCatalog
}

/** Tenant override precedes; remaining follow platform order. */
export function mergeTenantToolbarOrder(
  platformOrder: string[],
  tenantOverride: string[] | null | undefined,
): string[] {
  const platform = platformOrder.filter(Boolean)
  if (!tenantOverride || tenantOverride.length === 0) return [...platform]
  const platformSet = new Set(platform)
  const seen = new Set<string>()
  const out: string[] = []
  for (const t of tenantOverride) {
    if (!t || seen.has(t) || !platformSet.has(t)) continue
    out.push(t)
    seen.add(t)
  }
  for (const t of platform) {
    if (!seen.has(t)) {
      out.push(t)
      seen.add(t)
    }
  }
  return out
}

function reconcile(saved: string[]): string[] {
  return mergeTenantToolbarOrder(defaultToolbarOrder(), saved)
}

export function hasToolbarOrderOverride(): boolean {
  try {
    const raw = window.localStorage.getItem(TOOLBAR_ORDER_STORAGE_KEY)
    return raw != null && raw !== ''
  } catch {
    return false
  }
}

export function loadToolbarOrder(): string[] {
  try {
    const raw = window.localStorage.getItem(TOOLBAR_ORDER_STORAGE_KEY)
    if (!raw) return defaultToolbarOrder()
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed) || !parsed.every((v) => typeof v === 'string')) {
      return defaultToolbarOrder()
    }
    return reconcile(parsed)
  } catch {
    return defaultToolbarOrder()
  }
}

export function saveToolbarOrder(order: string[]): void {
  try {
    window.localStorage.setItem(TOOLBAR_ORDER_STORAGE_KEY, JSON.stringify(order))
  } catch {
    /* Private-browsing / storage-full — session-only reorder. */
  }
}

/** Clears tenant override so effective order dynamically inherits platform. */
export function clearToolbarOrder(): void {
  try {
    window.localStorage.removeItem(TOOLBAR_ORDER_STORAGE_KEY)
  } catch {
    /* noop */
  }
}

/**
 * Restore Default: delete override, return live platform order.
 * Must NOT write platform order back into localStorage (snapshot anti-pattern).
 */
export function resetToolbarOrderToPlatformDefault(): string[] {
  clearToolbarOrder()
  return defaultToolbarOrder()
}

export function moveItem<T>(arr: T[], from: number, to: number): T[] {
  if (from === to) return arr
  const next = [...arr]
  const [item] = next.splice(from, 1)
  const insertAt = to > from ? to - 1 : to
  next.splice(insertAt, 0, item)
  return next
}
